import { Link, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { SiteHeader } from "../platform/PlatformChrome";
import { Footer } from "../landing/Footer";
import { useDocument } from "../../lib/documents";
import { useCan, useMe } from "../../lib/auth";
import { usePreferences } from "../../lib/preferences";
import { BlockRenderer } from "../editor/BlockRenderer";
import { documentEditorConfig } from "../editor/documentConfig";
import type { BlockDoc, Block } from "../editor/types";
import { DocumentTrack } from "./DocumentTrack";
import { MaterialCollection } from "./MaterialCollection";
import { DocumentPlayer, useAudioQueue } from "./DocumentPlayer";
import { MediaModal } from "../media/MediaModal";
import {
  buildTabs,
  describeContents,
  formatBytes,
  groupMaterials,
  materialsIn,
  toMaterials,
  totalBytes,
} from "./materials";
import type { Material, MaterialTab } from "./materials";
import { Icon } from "../ui/Icon";
import { resolveApiUrl } from "../../lib/api";
import { folderSlug, parseFolderSlug, resolveFolder } from "@vedaunion/shared";
import type { DocumentTagNamespace, PublicDocumentTag, MaterialView } from "@vedaunion/shared";

/**
 * The public document surface — "Studio".
 *
 * A document in this Library is a folder of study materials that happens to have
 * an essay attached, not an essay that happens to have files attached. So the
 * page is built that way: an identity rail (cover, title, and the two things a
 * person actually came to do — recite it, or take it with you) beside a browser
 * of everything the document carries, in tiles or in rows. The prose lives in its
 * own tab.
 *
 * Every visual value comes from the tokens in `index.css`; this file contributes
 * no styling of its own beyond the class names of that centralised block.
 */

/**
 * The ONE document whose tile grid gives each kind its own line.
 *
 * Śrī Rudram is the only document that puts classes, recordings and texts in
 * one named group, so it is the only one where the 16:9 plates, the square
 * recordings and the portrait pages interleave on a line. The owner asked for
 * the break HERE and deliberately not as a library-wide rule, so it is opted
 * into by slug rather than derived: any other document lays out exactly as it
 * did before, and adding a second document to this behaviour is a decision
 * somebody makes on purpose, by name.
 */
const ONE_KIND_PER_LINE_SLUG = 'sri-rudram';

export function DocumentPage({ surface }: { surface: "public" | "dashboard" }) {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) {
    return (
      <main className="container-edit pt-32 pb-20">
        <p className="font-display-italic text-[var(--color-ink-mute)]">No document specified.</p>
      </main>
    );
  }

  if (surface === "public") {
    return (
      <>
        <SiteHeader />
        <main className="pt-28 sm:pt-32 pb-20 sm:pb-24">
          <DocumentBody slug={slug} surface="public" />
        </main>
        <Footer />
      </>
    );
  }

  return (
    <div>
      <DocumentBody slug={slug} surface="dashboard" />
    </div>
  );
}

function DocumentBody({ slug, surface }: { slug: string; surface: "public" | "dashboard" }) {
  const [params] = useSearchParams();
  /**
   * The open folder, from `?folder=namakam/anuvaka-1`. The URL is the state —
   * not a useState beside it — so a folder is a page: it can be shared, opened
   * in a new tab, reloaded, and left with the browser's back button.
   */
  const folderParam = params.get("folder");
  const [coverFailed, setCoverFailed] = useState(false);
  const [tab, setTab] = useState<MaterialTab | null>(null);
  const [watching, setWatching] = useState<Material | null>(null);
  const isPreviewParam = params.get("preview") === "1";
  const me = useMe();
  const docQ = useDocument(slug);
  const d = docQ.data?.document;
  const prefs = usePreferences();

  const canEditAny = useCan("documents.edit_any");
  const canEditOwn = useCan("documents.edit_own");

  const materials = useMemo(
    () => toMaterials(d?.attachments ?? [], d?.slug ?? ""),
    [d?.attachments, d?.slug],
  );
  const audio = useAudioQueue(materials);

  const folderSlugs = useMemo(() => parseFolderSlug(folderParam), [folderParam]);
  // The folder's real names (`["Namakam", "Anuvāka 1"]`), or null when the link
  // names a folder that is not there (renamed, emptied).
  const folder = useMemo(
    () => resolveFolder(materials.map((m) => m.path), folderSlugs),
    [materials, folderSlugs],
  );
  const inFolder = folderSlugs.length > 0;
  const folderName = folder && folder.length > 0 ? folder[folder.length - 1]! : null;

  /**
   * Opening a folder brings the panel into view — only when it is out of view.
   * A folder link opened on a phone lands below the cover, so it scrolls down to
   * the folder; on a desktop the panel stands beside the cover and nothing moves.
   * Later, it scrolls only when the panel's top has gone off screen.
   */
  const firstFolderScroll = useRef(true);
  useEffect(() => {
    if (!d || !folderParam) return;
    const el = document.getElementById("content");
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const first = firstFolderScroll.current;
    firstFolderScroll.current = false;
    if (top < 0 || top > window.innerHeight * 0.5) {
      el.scrollIntoView({ behavior: first ? "auto" : "smooth", block: "start" });
    }
  }, [d, folderParam]);

  /**
   * The tab title names the folder too. Link previews are written by the server
   * (`server/src/lib/social-meta.ts`) — a chat app never runs this — but the
   * browser tab, the history list and a bookmark read it from here.
   */
  useEffect(() => {
    if (!d) return;
    const before = document.title;
    document.title = folderName ? `${folderName} · ${d.title} — Veda Union` : `${d.title} — Veda Union`;
    return () => {
      document.title = before;
    };
  }, [d, folderName]);

  /**
   * `#content` opens the page at the materials.
   *
   * Same shareable-fragment contract as `#chant`: a link like
   * `/library/sri-rudram#content` lands on the files rather than on the essay,
   * which on a phone is most of a screen away behind the cover, the blurb and
   * the buttons. The browser cannot honour the fragment by itself — the panel
   * does not exist yet when the fragment is read, because the document arrives
   * over the network — so it is done once the document is in.
   */
  const { hash } = useLocation();
  useEffect(() => {
    if (!d || hash !== "#content") return;
    document.getElementById("content")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [d, hash]);

  const backTo = surface === "dashboard" ? "/dashboard/library/documents" : "/library";

  if (docQ.isLoading) {
    return (
      <div className="container-edit max-w-[64rem]">
        <p className="font-display-italic text-[var(--color-ink-mute)]">Loading…</p>
      </div>
    );
  }
  if (!d) {
    return (
      <div className="container-edit max-w-[64rem]">
        <h2 className="font-display text-[1.85rem] leading-tight mb-4">Not found.</h2>
        <Link to={backTo} className="row-action inline-flex items-center gap-2">
          <Icon name="nav-left" size="1em" /> <span>Back to Library</span>
        </Link>
      </div>
    );
  }

  const isOwn = !!me.data?.user && d.authorId === me.data.user.id;
  const canEdit = canEditAny || (canEditOwn && isOwn);
  const showPreviewBanner = isPreviewParam || d.status !== "published";

  const tagsByNs = d.tags.reduce<Record<DocumentTagNamespace, PublicDocumentTag[]>>(
    (acc, t) => {
      acc[t.namespace] = acc[t.namespace] ?? [];
      acc[t.namespace].push(t);
      return acc;
    },
    { type: [], deity: [], topic: [] },
  );

  // The body is split once: the chant block is the "recite" affordance and
  // belongs at the top of the materials panel; everything else is the essay and
  // belongs in About. Nothing is dropped — the two halves partition the blocks.
  const bdoc = d.contentJson as BlockDoc | null;
  const allBlocks: Block[] = bdoc && Array.isArray(bdoc.blocks) ? bdoc.blocks : [];
  const chantBlocks = allBlocks.filter((b) => b.type === "chant");
  const proseBlocks = allBlocks.filter((b) => b.type !== "chant");

  // Inside a folder, everything below is about the folder: its tabs, its
  // counts, its download. Outside one, the whole document, as before.
  const scope = inFolder ? materialsIn(materials, folderSlugs) : materials;
  const tabs = buildTabs(scope, { about: !inFolder });
  // A tab picked elsewhere may not exist here (a folder with no video) — fall
  // back to the first tab rather than showing an empty one.
  const activeTab: MaterialTab =
    tab && tabs.some((t) => t.key === tab) ? tab : tabs[0]?.key ?? "about";
  const groups = groupMaterials(materials, activeTab, folder ?? []);
  /** The page itself, at a folder (or at the top for ""). Search only: the
   *  route, and with it the document, stays where it is. */
  const folderLink = (slugPath: string) => ({ search: slugPath ? `?folder=${slugPath}` : "" });

  const reader =
    chantBlocks.length > 0 && bdoc ? (
      <div className={inFolder ? "mt-8" : "mb-6"}>
        <BlockRenderer doc={{ ...bdoc, blocks: chantBlocks }} config={documentEditorConfig} />
      </div>
    ) : null;

  const view: MaterialView = prefs.prefs.library.view;
  const setView = (v: MaterialView) => prefs.update({ library: { view: v } });

  // Everything the zip route will actually include — so the button can state a
  // real size instead of promising a download that turns out to be empty.
  const downloadable = materials.filter((m) => m.downloadUrl);
  const downloadBytes = d.attachments
    .filter((a) => a.fileUrl && ["pdf", "audio", "video", "file"].includes(a.kind))
    .reduce((sum, a) => sum + (a.bytes ?? 0), 0);

  return (
    <div
      className={`container-edit max-w-[80rem] doc-studio-page${audio.current ? " has-player" : ""}`}
    >
      {showPreviewBanner && (
        <div className="veda-banner is-preview mb-7">
          <p className="font-grotesk text-[0.7rem] tracking-[0.16em] uppercase text-[var(--color-violet)]">
            Preview · status: {d.status}
          </p>
          {canEdit && (
            <Link
              to={`/dashboard/documents/${d.slug}/edit`}
              className="inline-flex items-center gap-1.5 font-grotesk text-[0.7rem] tracking-[0.16em] uppercase text-[var(--color-ink)] hover:text-[var(--color-violet)]"
            >
              Continue editing <Icon name="arrow-right" size="1em" />
            </Link>
          )}
        </div>
      )}

      <Link
        to={backTo}
        className="inline-flex items-center gap-1.5 font-grotesk text-[0.68rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)] hover:text-[var(--color-violet)] transition-colors mb-6"
      >
        <Icon name="nav-left" size="1em" /> Library
      </Link>

      <div className="doc-studio">
        <aside className="doc-ident">
          <div className="media-frame doc-ident__cover">
            {d.coverImage && !coverFailed ? (
              <img src={d.coverImage} alt="" onError={() => setCoverFailed(true)} />
            ) : (
              <div className="w-full h-full grid place-items-center text-[var(--color-ink-light)]">
                <Icon name="book-stack" size="2rem" />
              </div>
            )}
          </div>

          <div className="doc-ident__head">
            {tagsByNs.type[0] && (
              <p className="page-eyebrow mb-1.5">
                {tagsByNs.type[0].label}
                {tagsByNs.deity[0] ? ` · ${tagsByNs.deity[0].label}` : ""}
              </p>
            )}
            <h1 className="doc-ident__title">{d.title}</h1>
            {d.subtitle && <p className="doc-ident__subtitle">{d.subtitle}</p>}
          </div>

          {d.excerpt && <Blurb html={d.excerpt} />}

          <div className="doc-ident__actions">
            {chantBlocks.length > 0 && (
              // A link, not a button: the fragment is the reader's own deep-link
              // contract (ChantEmbed opens on #chant), so this is shareable and
              // survives a reload.
              <a className="btn-app" href="#chant">
                <Icon name="play" size="1em" /> Recite
              </a>
            )}
            {downloadable.length > 0 && (
              <a
                className="btn-app-ghost"
                href={resolveApiUrl(`/api/documents/${encodeURIComponent(d.slug)}/download`)}
                // The server sends Content-Disposition: attachment; this makes
                // the intent explicit for the browser and for a screen reader.
                download
              >
                <Icon name="download" size="1em" />
                Download all
                {downloadBytes > 0 ? ` · ${formatBytes(downloadBytes)}` : ""}
              </a>
            )}
          </div>

          {(tagsByNs.deity.length > 0 || tagsByNs.topic.length > 0) && (
            <div className="doc-ident__tags">
              {[...tagsByNs.deity, ...tagsByNs.topic].map((t) => (
                <Link
                  key={t.id}
                  to={`/library/documents?tag=${encodeURIComponent(`${t.namespace}:${t.slug}`)}`}
                  className="chip"
                >
                  {t.label}
                </Link>
              ))}
            </div>
          )}
        </aside>

        <div className="min-w-0" id="content">
          {folder && folderName && (
            <FolderHeader
              path={folder}
              summary={describeContents(scope)}
              crumbTo={(depth) => folderLink(depth === 0 ? "" : folderSlug(folder.slice(0, depth)))}
              downloadUrl={
                scope.some((m) => m.downloadUrl)
                  ? resolveApiUrl(
                      `/api/documents/${encodeURIComponent(d.slug)}/download?folder=${encodeURIComponent(folderSlug(folder))}`,
                    )
                  : null
              }
              downloadBytes={totalBytes(scope)}
              shareTitle={`${folderName} · ${d.title}`}
            />
          )}
          <div className="mat-toolbar">
            <div className="mat-toolbar__tabs" role="tablist" aria-label="Materials">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === t.key}
                  className={`subtab ${activeTab === t.key ? "is-active" : ""}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                  {t.count !== null && <span className="subtab__count">{t.count}</span>}
                </button>
              ))}
            </div>
            {activeTab !== "about" && groups.length > 0 && (
              <div className="mat-toolbar__right">
                <ViewToggle view={view} onChange={setView} />
              </div>
            )}
          </div>

          {/* The reader is not a file, so it sits above the list rather than
              inside it — and it is the SAME embed the block editor renders,
              never a second copy of that affordance.

              It is rendered for EVERY tab, not only the material ones. The rail's
              "Recite" works by setting the #chant fragment, which only opens a
              mounted embed; a document whose sole tab is About (a manual with no
              attachments, e.g. Pūjā Vidhi) would otherwise show a Recite button
              that does nothing.

              Inside a folder it moves BELOW the files: a folder is opened for
              what is in it, and the reader is the whole text, not the folder's. */}
          {!inFolder && reader}

          {inFolder && !folder ? (
            <div className="veda-empty is-dashed">
              <p className="font-grotesk text-[0.85rem] text-[var(--color-ink-mute)]">
                This folder is not here any more — it may have been renamed.{" "}
                <Link to={folderLink("")} className="text-[var(--color-violet)] underline">
                  See all materials
                </Link>
              </p>
            </div>
          ) : activeTab === "about" ? (
            <AboutPanel doc={bdoc} blocks={proseBlocks} htmlFallback={d.contentHtml} />
          ) : (
            <>
              {groups.length > 0 ? (
                <MaterialCollection
                  groups={groups}
                  view={view}
                  oneKindPerLine={d.slug === ONE_KIND_PER_LINE_SLUG}
                  playingId={audio.current?.id ?? null}
                  onPlay={audio.play}
                  onWatch={(m) => {
                    // One at a time: a class recording and the audio bar
                    // playing together is two voices in the room.
                    audio.stop();
                    setWatching(m);
                  }}
                  folderTo={(f) => folderLink(f.key)}
                />
              ) : (
                <div className="veda-empty is-dashed">
                  <p className="font-grotesk text-[0.85rem] text-[var(--color-ink-mute)]">
                    No materials have been attached to this document yet.
                  </p>
                </div>
              )}
            </>
          )}

          {inFolder && reader}

          {d.relatedEvents.length > 0 && (
            <section className="mt-12 pt-9 border-t border-[var(--color-rule)]">
              <p className="page-eyebrow mb-2">Practice with us</p>
              <h2 className="font-display text-[1.6rem] leading-[1.18] tracking-tight mb-5">
                Upcoming events around this document
              </h2>
              <ul className="flex flex-col">
                {d.relatedEvents.map((ev) => (
                  <li key={ev.id} className="border-t border-[var(--color-rule)] first:border-t-0 py-3">
                    <Link to={`/calendar/${ev.slug}`} className="block group">
                      <p className="font-grotesk lining text-[0.66rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)]">
                        {format(new Date(ev.startsAt), "EEEE, d LLLL yyyy · HH:mm")}
                        <span className="mx-2">·</span>
                        {ev.locationType}
                      </p>
                      <p className="font-display text-[1.2rem] leading-[1.2] tracking-tight mt-1 group-hover:text-[var(--color-violet)] transition-colors">
                        {ev.title}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {d.relatedDocuments.length > 0 && (
            <section className="mt-12 pt-9 border-t border-[var(--color-rule)]">
              <p className="page-eyebrow mb-2">See also</p>
              <h2 className="font-display text-[1.6rem] leading-[1.18] tracking-tight mb-5">
                Adjacent documents
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                {d.relatedDocuments.map((rd) => (
                  <li key={rd.id}>
                    <DocumentTrack
                      to={`/library/${rd.slug}`}
                      coverImage={rd.coverImage}
                      title={rd.title}
                      subtitle={rd.subtitle}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>

      <DocumentPlayer queue={audio} documentTitle={d.title} />

      {watching?.videoSource && (
        <MediaModal
          source={watching.videoSource}
          title={watching.title}
          meta={watching.meta}
          poster={watching.thumbnailUrl}
          downloadUrl={watching.downloadUrl}
          onClose={() => setWatching(null)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The head of an open folder: where it is (each level a link back up), what it
 * holds, and the two things a class needs from it — pass it on, and take it
 * home. "Share" is the phone's own share sheet where there is one (straight to
 * WhatsApp) and a copied link everywhere else.
 */
function FolderHeader({
  path,
  summary,
  crumbTo,
  downloadUrl,
  downloadBytes,
  shareTitle,
}: {
  path: string[];
  summary: string;
  crumbTo: (depth: number) => { search: string };
  downloadUrl: string | null;
  downloadBytes: number;
  shareTitle: string;
}) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);

  const share = async () => {
    // The page's own address, which already carries `?folder=`; the hash is
    // dropped so the link opens on the folder rather than mid-page.
    const url = window.location.href.split("#")[0]!;
    if (typeof navigator.share === "function" && window.matchMedia("(pointer: coarse)").matches) {
      try {
        await navigator.share({ title: shareTitle, url });
        return;
      } catch (err) {
        if ((err as DOMException | undefined)?.name === "AbortError") return; // sheet closed
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      window.prompt("Copy this link", url);
    }
  };

  return (
    <header className="mat-folder">
      <nav className="mat-folder__crumbs" aria-label="Folder">
        <Link to={crumbTo(0)} className="mat-folder__crumb">
          <Icon name="nav-left" size="1em" />
          All materials
        </Link>
        {path.slice(0, -1).map((seg, i) => (
          <span key={seg} className="mat-folder__crumb-wrap">
            <span className="mat-folder__sep" aria-hidden="true">/</span>
            <Link to={crumbTo(i + 1)} className="mat-folder__crumb">
              {seg}
            </Link>
          </span>
        ))}
      </nav>
      <div className="mat-folder__row">
        <div className="mat-folder__id">
          <span className="mat-folder__icon" aria-hidden="true">
            <Icon name="folder" size="1.3rem" />
          </span>
          <div className="min-w-0">
            <h2 className="mat-folder__title">{path[path.length - 1]}</h2>
            {summary && <p className="mat-folder__meta">{summary}</p>}
          </div>
        </div>
        <div className="mat-folder__actions">
          <button type="button" className="btn-app-ghost" onClick={share}>
            <Icon name={copied ? "check" : "link"} size="1em" />
            <span aria-live="polite">{copied ? "Link copied" : "Share"}</span>
          </button>
          {downloadUrl && (
            <a className="btn-app-ghost" href={downloadUrl} download>
              <Icon name="download" size="1em" />
              Download
              {downloadBytes > 0 ? ` · ${formatBytes(downloadBytes)}` : ""}
            </a>
          )}
        </div>
      </div>
    </header>
  );
}

/**
 * The description under the title. Clamped to three lines so the materials stay
 * above the fold on a phone, with the rest one tap away. The clamp is CSS, so the
 * full text is always in the DOM for search engines and screen readers.
 */
function Blurb({ html }: { html: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="doc-ident__blurb">
      <div
        className={`doc-blurb__body${open ? "" : " is-clamped"}`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <button type="button" className="doc-blurb__more" onClick={() => setOpen((v) => !v)}>
        {open ? "Read less" : "Read more"}
        <Icon name={open ? "nav-left" : "nav-right"} size="0.9em" />
      </button>
    </div>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: MaterialView;
  onChange: (v: MaterialView) => void;
}) {
  return (
    <div className="view-toggle" role="group" aria-label="View">
      <button
        type="button"
        className="view-toggle__btn"
        aria-pressed={view === "grid"}
        aria-label="Grid view"
        title="Grid view"
        onClick={() => onChange("grid")}
      >
        <Icon name="view-grid" size="1.05em" />
      </button>
      <button
        type="button"
        className="view-toggle__btn"
        aria-pressed={view === "list"}
        aria-label="List view"
        title="List view"
        onClick={() => onChange("list")}
      >
        <Icon name="view-list" size="1.05em" />
      </button>
    </div>
  );
}

function AboutPanel({
  doc,
  blocks,
  htmlFallback,
}: {
  doc: BlockDoc | null;
  blocks: Block[];
  htmlFallback: string;
}) {
  if (!doc || blocks.length === 0) {
    return (
      <div className="veda-empty is-dashed">
        <p className="font-grotesk text-[0.85rem] text-[var(--color-ink-mute)]">
          No description has been written for this document yet.
        </p>
      </div>
    );
  }
  return (
    <BlockRenderer doc={{ ...doc, blocks }} config={documentEditorConfig} htmlFallback={htmlFallback} />
  );
}
